import styled, { css } from 'styled-components';
import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { myPriceDiscountPercentageStyles } from '@/app/components/search/components/Card/Details/Price/PriceDetails/styled.ts';
import { darkLava, pinkPeople, pinkPeople50, wegDe } from '../../../../../styles/colors';
import type { Localization } from '../../../../../common/Localization';

/** The largest size a badge draws its icon at, which is what a named icon is requested at. */
export const BIGGER_ICON_SIZE = 24;

const biggerIcon = css`
  background-size: ${BIGGER_ICON_SIZE}px ${BIGGER_ICON_SIZE}px;
  padding-left: 0;
  padding-right: 30px;
  background-position: right;
`;

const iconToTheRight = css`
  padding-right: 30px;
  padding-left: 8px;
  background-position: right 8px center;
`;

const handlePromotionBadgeStyles = (promotionType: PROMOTION_TYPES | null, localization: Localization) => {
  switch (promotionType) {
    case PROMOTION_TYPES.APP_ONLY_PRICE:
    case PROMOTION_TYPES.MOBILE_ONLY_PRICE:
    case PROMOTION_TYPES.JANUARY_SALE:
      return css`
        background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
      `;
    case PROMOTION_TYPES.PINK_PEOPLE:
      return css`
        background-color: ${pinkPeople50};
        color: ${pinkPeople};
      `;
    case PROMOTION_TYPES.TO_LASTMINUTE:
      return css`
        background-color: ${pinkPeople};
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
      `;
    case PROMOTION_TYPES.TOP_SECRET_HOTELS:
      return css`
        background-color: ${({ theme }) => theme.kallisto.color.common.special.topSecretHotels[900]};
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
        font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
      `;
    case PROMOTION_TYPES.TOUR_DE_FRANCE:
    case PROMOTION_TYPES.LONDON_EYE:
      return css`
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
        background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralSubtle};
      `;
    case PROMOTION_TYPES.MY_PRICE:
      return css`
        background-color: ${({ theme }) => theme.kallisto.color.common.special.myPrice[500]};
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};

        &.my-price-percentage {
          ${myPriceDiscountPercentageStyles};
        }
      `;
    case PROMOTION_TYPES.TO_WEGDE:
      return css`
        background-color: ${wegDe};
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
        font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
      `;
    case PROMOTION_TYPES.FLASH_SALES:
      return css`
        background-color: ${
          localization.configValuesApp.lastMinuteDeals
            ? ({ theme }) => theme.kallisto.color.background.static.backgroundLayer01
            : ({ theme }) => theme.kallisto.color.common.special.flashSales[400]
        };
        color: ${
          localization.configValuesApp.lastMinuteDeals
            ? ({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault
            : ({ theme }) => theme.kallisto.color.background.static.backgroundPage
        };
      `;
    case PROMOTION_TYPES.BLACK_FRIDAY:
    case PROMOTION_TYPES.CYBER_MONDAY:
    case PROMOTION_TYPES.TRAVEL_TUESDAY:
    case PROMOTION_TYPES.SUMMER_SALE:
    case PROMOTION_TYPES.AUTUMN_SALES:
      return css`
        background-color: ${darkLava};
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
      `;

    case PROMOTION_TYPES.PACKAGE_SAVINGS:
      return css`
        background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
        font-weight: bold;
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
        background-size: 16px;
        ${iconToTheRight};
      `;
    case PROMOTION_TYPES.VOUCHER_EXTRA:
      return css`
        color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
        font-size: ${({ theme }) => theme.kallisto.font.size['150']};
        font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
        line-height: 10px;
        text-align: right;
        ${iconToTheRight};
        ${biggerIcon};
        display: block;
      `;
    default:
      return css`
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      `;
  }
};

export const labelWithIconBaseStyles = css`
  border-radius: 4px;
  min-height: 28px;
  padding: 4px 8px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  font-style: normal;
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};
  margin-bottom: 4px;
  margin-left: 4px;

  &:first-child {
    margin-left: 0;
  }

  &:only-child {
    @media (min-width: 768px) {
      margin-bottom: 0;
    }
  }
`;

export const ICON_SLOT = 14;

export const LabelWithIconContainer = styled.div<{
  promotionType: PROMOTION_TYPES | null;
  icon?: string;
  iconSize?: number;
  fontWeight?: string;
  localization: Localization;
}>`
  ${labelWithIconBaseStyles};

  ${({ icon }) =>
    icon &&
    css`
      padding-left: 30px;
      background-image: url(${icon});
      background-repeat: no-repeat;
      background-position: 8px center;
      background-size: ${ICON_SLOT}px;
    `};

  ${({ iconSize }) =>
    iconSize &&
    css`
      background-position: ${8 + (ICON_SLOT - iconSize) / 2}px center;
      background-size: ${iconSize}px;
    `};

  ${({ promotionType, localization }) => promotionType && handlePromotionBadgeStyles(promotionType, localization)};

  img {
    margin-right: 8px;
  }
`;
